import { Dispatch, UnknownAction } from "@reduxjs/toolkit";
import { IAxiosDefaultDataObject } from "../../config";
import BaseApp, { IBaseApp, IBaseAppDataDisplayerConfig, IBaseAppDynamicBasePage, IBaseAppFormConfig, IGridDataAdditional } from "../BaseApp";
import { GridDataColumns, IGridDataConfig } from "../../components/GridData";
import { ICellRendererParams } from "ag-grid-community";
import { Link } from "react-router-dom";
import { IUpdateModel } from ".";
import { addNewUpdate, deleteUpdate, setSelectedUpdates, updateAllUpdates, updateUpdate, updateUpdatesLoadingStatus, updateUpdatesPagination, updateUpdatesSearch, updateUpdatesSearchParams, useReturnAllUpdates, useReturnSelectedUpdates, useReturnUpdatesLoadingStatus, useReturnUpdatesPagination, useReturnUpdatesSearch, useReturnUpdatesSearchParams } from "./updatesSlice";
import api from "../../services/api";

export const EmptyUpdateModel: IUpdateModel = {
    id: 0,
    title: "",
    post: "",
    created_by: 0,
    date_created: "",
    last_updated: null,
    is_active: false,
    slug: "",
    created_by_name: "",
}

/**
 * Updates App
 */
export default class UpdatesApp extends BaseApp implements IBaseApp {

    appName = "updates"
    appBaseEndPoint = "/updates"

    // customListEndPoint: string = this.appBaseEndPoint

    messages = {
        create: "Create a new update",
        createShort: "New update",
        edit: "Edit update",
        editShort: "Edit",
        delete: "Delete update",
        deleteShort: "Delete",
        view: "View update",
        nothingFound: "No updates have been found.",
    }

    elemPrefix: string = "update_"

    dataDisplayer: IBaseAppDataDisplayerConfig = {
        columns: [
            {
                id: "title",
                type: "text",
                label: "Title"
            },
        ],
        notionFound: {
            title: "Nothing...",
            message: "No updates have been found :("
        }
    }

    formConfig: IBaseAppFormConfig = {
        form: {
            name: "brands_form",
            elements: [
                {
                    id: this.elemPrefix + "title",
                    xs: 12,
                    type: "TextField",
                    label: "Title",
                    fullWidth: true
                },
                {
                    id: this.elemPrefix + "post",
                    xs: 12,
                    type: "BlockEditor",
                    label: "Post",
                    fullWidth: true,
                },
            ]
        },
        sectionBox: {
            showTitle: true,
            title: "Details",
            description: "Fill in the update details"
        },
        requiredFields: [
            {
                id: this.elemPrefix + "title",
                message: "Title is required!"
            },
        ]
    }

    /**
     * Redux
     */
    redux = {
        updateItem: (item: IAxiosDefaultDataObject, dispatch: Dispatch<UnknownAction>) => dispatch(updateUpdate((item as IUpdateModel))),
        addNewItem: (item: IAxiosDefaultDataObject, dispatch: Dispatch<UnknownAction>) => dispatch(addNewUpdate((item as IUpdateModel)))
    }

    /**
     * Dynamic Base Page
     */
    dynamicBasePage: IBaseAppDynamicBasePage = {
        items: useReturnAllUpdates,
        pagination: useReturnUpdatesPagination,
        loading: useReturnUpdatesLoadingStatus,
        search: useReturnUpdatesSearch,
        updateLoadingStatus: (status: boolean, dispatch: Dispatch<UnknownAction>) => dispatch(updateUpdatesLoadingStatus({ loading: status })),
        deleteItem: (id: number, dispatch: Dispatch<UnknownAction>) => dispatch(deleteUpdate({ id: id })),
        addNewItem: (item, dispatch) => dispatch(addNewUpdate((item as IUpdateModel))),
        updateItem: (item, dispatch) => dispatch(updateUpdate((item as IUpdateModel))),
        updateAllItems: (items, dispatch) => dispatch(updateAllUpdates((items as IUpdateModel[]))),
        updateSearch: (search: string, dispatch: Dispatch<UnknownAction>) => dispatch(updateUpdatesSearch({ search: search })),
        updateItemsPagination: (pagination, dispatch) => dispatch(updateUpdatesPagination(pagination)),
        itemsActionOptions: [
            {
                icon: "edit",
                label: "Edit",
                link: "/panel/updates/edit/[id]",
                onClick: (rawItem) => {
                    // navigate("/panel/items/edit/" + rawItem.id)
                },
            },
            {
                icon: "show",
                label: "View",
                link: "/panel/updates/view/[id]",
                onClick: (rawItem) => {
                    // navigate("/panel/items/view/" + rawItem.id)
                },
            },
        ],
        // filterForm: [],
        // customHeaderOptionActionButtons: [],
        // viewItemTemplateLink: "",
        // disableAutoViewItemLink: false,
        // hidePageHeadSection: false,
        // customTitlePageHeadSection: "",
        // disableSearch: false,
        // customPageHeadSectionActionButtons: []
    }

  /**
     * Grid data
     */
    gridDataColumns: GridDataColumns = [
        // {
        //     field: "thumbnail",
        //     headerName: "thumbnail",
        //     type: "thumbnail",
        //     resizable: false,
        //     width: 110,
        // },
        // {
        //     field: "title",
        //     headerName: "Title",
        //     width: 250,
        //     cellRenderer: (params: ICellRendererParams<IUpdateModel>) => {
        //         const item = params.data

        //         if (!item){
        //             return params.value
        //         }

        //         return(
        //             <div className="grid-data-current-item-location-box">
        //                 <Link className="data-grid-link" to={`/panel/updates/view/${item.id}`}>
        //                     <h3 className="title">{item.title}</h3>
        //                 </Link>
        //             </div>
        //         )
        //     }
        // },
                {
            field: "title",
            headerName: "Update",
            type: "title",
            width: 350,
            minWidth: 350,
            cellRendererParams: {
                appClass: () => new UpdatesApp(),
                enableQuickPreview: true,
            }
        },
        {
            field: "created_by_name",
            headerName: "Created by",
            type: "text",
            width: 250,
        },
        {
            field: "date_created",
            headerName: "Date Created",
            type: "date",
            width: 250,
        },
        {
            field: "last_updated",
            headerName: "Last updated",
            type: "date",
            width: 250,
        }
    ]
    useGridData: boolean = true
    gridConfig: IGridDataConfig = {
        viewItemLink: "/panel/updates/view/[id]",
        editItemLink: "/panel/updates/edit/[id]",
    }
    gridDataEssentials: IGridDataAdditional  = {
        selectedItems: useReturnSelectedUpdates,
        searchParams: useReturnUpdatesSearchParams,
        actionButtons: [],
        headerOptions: [],
        setSelectedItems: (items, dispatch) => dispatch(setSelectedUpdates(items)),
        updateItemSearchParams: (params, dispatch) => dispatch(updateUpdatesSearchParams(params))
    }

    /**
     * Latest update
     */
    latestUpdate = async() => {
        const response = await api.get(this.appBaseEndPoint + "/latest", {})
        return response
    }

}